import React from 'react';
import { Form, Input, InputNumber, Card, Spin, Upload, Image, Space, Alert, Row, Col } from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';

import PageLayout from '@components/layout/PageLayout';

import styles from './NspCoreInfoPage.module.css';
import useNspCoreInfoPage from './useNspCoreInfoPage';
import { NSPButton } from '@components/ui/NSPButton';
import useSession from '@hooks/useSession';


const NspCoreInfoPage = () => {
  const { isAdmin, isSuperAdmin } = useSession();
  const isReadOnly = isAdmin && !isSuperAdmin;

  const {
    form,
    selectedEntry,
    loading,
    updating,
    handleSave,
    handleLogoUpload,
    fileList,
    uploadingLogo,
    setFileList,
    saveFeedback,
  } = useNspCoreInfoPage();

  if (loading) {
    return (
      <PageLayout title="NSP Core Information">
        <div className={styles.loadingContainer} data-testid="page-loading">
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!selectedEntry) {
    return (
      <PageLayout title="NSP Core Information">
        <Card data-testid="nsp-core-info-empty">
          <p>No core information entries found. Please create an entry in the database first.</p>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="NSP Core Information">
      <Card className={styles.card} data-testid="nsp-core-info-card" data-read-only={String(isReadOnly)}>
        {saveFeedback && (
          <Alert
            message={
              saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'
            }
            description={saveFeedback.message}
            type={saveFeedback.status}
            showIcon
            closable
            style={{ marginBottom: 16 }}
            data-testid="save-feedback-alert"
            data-type={saveFeedback.status}
          />
        )}
        <Form form={form} layout="vertical" onFinish={handleSave} className={styles.form} disabled={isReadOnly} data-testid="nsp-core-info-form">
          {/* Company Logo Section */}
          <div className={styles.logoSection} data-testid="logo-section">
            <h3 data-testid="section-heading" data-section="company-logo">Company Logo</h3>
            <Space direction="vertical" align="center">
              {selectedEntry.logoUrl && (
                <Image
                  src={selectedEntry.logoUrl}
                  alt="Company Logo"
                  width={200}
                  preview
                  className={styles.logoImage}
                  data-testid="company-logo"
                />
              )}
              {!isReadOnly && (
                <Upload
                  beforeUpload={handleLogoUpload}
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                  data-testid="logo-file-input"
                >
                  <NSPButton
                    icon={<UploadOutlined />}
                    loading={uploadingLogo}
                    data-testid="logo-upload-button"
                    data-action={selectedEntry.logoUrl ? 'replace' : 'upload'}
                  >
                    {selectedEntry.logoUrl ? 'Replace Logo' : 'Upload Logo'}
                  </NSPButton>
                </Upload>
              )}
            </Space>
          </div>

          {/* Company Information */}
          <h3 data-testid="section-heading" data-section="company-information">Company Information</h3>
          <Form.Item
            label="Company Name"
            name="companyName"
            rules={[{ required: true, message: 'Please enter company name' }]}
            data-testid="form-field"
            data-field="company-name"
          >
            <Input data-testid="company-name-input" />
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: 'Please enter address' }]}
            data-testid="form-field"
            data-field="address"
          >
            <Input.TextArea rows={2} data-testid="address-input" />
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="City"
                name="city"
                rules={[{ required: true, message: 'Please enter city' }]}
                data-testid="form-field"
                data-field="city"
              >
                <Input data-testid="city-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Province"
                name="province"
                rules={[{ required: true, message: 'Please enter province' }]}
                data-testid="form-field"
                data-field="province"
              >
                <Input data-testid="province-input" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Postal Code"
                name="postalCode"
                rules={[
                  { required: true, message: 'Please enter postal code' },
                  {
                    pattern: /^[A-Z]\d[A-Z] \d[A-Z]\d$/,
                    message: 'Please enter a valid Canadian postal code (e.g., A1A 1A1)',
                  },
                ]}
                data-testid="form-field"
                data-field="postal-code"
              >
                <Input placeholder="A1A 1A1" data-testid="postal-code-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Fax Number"
                name="faxNumber"
                rules={[
                  { required: true, message: 'Please enter fax number' },
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                    message: 'Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890)',
                  },
                ]}
                data-testid="form-field"
                data-field="fax-number"
              >
                <Input placeholder="(###) ###-####" data-testid="fax-number-input" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="HST Rate (%)"
                name="hstRate"
                rules={[
                  { required: true, message: 'Please enter HST rate' },
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: 'HST rate must be between 0 and 100',
                  },
                ]}
                data-testid="form-field"
                data-field="hst-rate"
              >
                <InputNumber style={{ width: '100%' }} precision={2} placeholder="15.00" data-testid="hst-rate-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email Address for Approver"
                name="approverEmail"
                rules={[
                  { required: true, message: 'Please enter approver email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
                data-testid="form-field"
                data-field="approver-email"
              >
                <Input placeholder="approver@example.com" data-testid="approver-email-input" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Email Address for Energy Risk Management Point of Contact"
                name="energyRiskManagementEmail"
                rules={[
                  { required: true, message: 'Please enter Energy Risk Management email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
                data-testid="form-field"
                data-field="energy-risk-management-email"
              >
                <Input placeholder="energyrisk@example.com" data-testid="energy-risk-management-email-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="NSP GL"
                name="nspGl"
                rules={[
                  { required: true, message: 'NSP GL is required' },
                  { pattern: /^[0-9.]+$/, message: 'Only numbers and periods are allowed' },
                ]}
                data-testid="form-field"
                data-field="nsp-gl"
              >
                <Input placeholder="e.g., 001.100100.010.1100.010.000.0000.000" data-testid="nsp-gl-input" />
              </Form.Item>
            </Col>
          </Row>

          {/* Contact Information */}
          <h3 data-testid="section-heading" data-section="contact-information">Contact Information</h3>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Contact Name"
                name="contactName"
                rules={[{ required: true, message: 'Please enter contact name' }]}
                data-testid="form-field"
                data-field="contact-name"
              >
                <Input data-testid="contact-name-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contact Phone"
                name="contactPhone"
                rules={[
                  { required: true, message: 'Please enter contact phone' },
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                    message: 'Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890)',
                  },
                ]}
                data-testid="form-field"
                data-field="contact-phone"
              >
                <Input placeholder="(###) ###-####" data-testid="contact-phone-input" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Contact Fax"
                name="contactFax"
                rules={[
                  { required: true, message: 'Please enter contact fax' },
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                    message: 'Please enter a valid phone number (e.g., (123) 456-7890 or 123-456-7890)',
                  },
                ]}
                data-testid="form-field"
                data-field="contact-fax"
              >
                <Input placeholder="(###) ###-####" data-testid="contact-fax-input" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contact Email"
                name="contactEmail"
                rules={[
                  { required: true, message: 'Please enter contact email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
                data-testid="form-field"
                data-field="contact-email"
              >
                <Input data-testid="contact-email-input" />
              </Form.Item>
            </Col>
          </Row>

          {/* Save Button */}
          {!isReadOnly && (
            <Form.Item>
              <NSPButton
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={updating}
                size="large"
                data-testid="save-changes-button"
              >
                Save Changes
              </NSPButton>
            </Form.Item>
          )}
        </Form>
      </Card>
    </PageLayout>
  );
};

export default NspCoreInfoPage;
